import { useTranslation } from 'react-i18next';
import { useTheme } from 'next-themes';
import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';

const OPTIONS = [
  { value: 'light', icon: Sun, labelKey: 'userMenu.themeLight' },
  { value: 'dark', icon: Moon, labelKey: 'userMenu.themeDark' },
  { value: 'system', icon: Monitor, labelKey: 'userMenu.themeSystem' },
] as const;

interface ThemeSwitcherProps {
  className?: string;
}

/** Light / dark / system theme picker as a segmented control. */
export function ThemeSwitcher({ className }: ThemeSwitcherProps) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const current = theme ?? 'light';

  return (
    <div role="radiogroup" aria-label={t('userMenu.theme')} className={cn('grid grid-cols-3 gap-1 rounded-xl bg-muted/70 p-1', className)}>
      {OPTIONS.map(({ value, icon: Icon, labelKey }) => {
        const selected = current === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => setTheme(value)}
            className={cn(
              'min-h-11 min-w-0 flex flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[11px] font-bold leading-tight transition-colors cursor-pointer',
              selected ? 'bg-card text-primary shadow-xs' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className="size-4 shrink-0" />
            <span className="whitespace-nowrap">{t(labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}
